/**
 * User Form Component
 *
 * Modal form for editing user details.
 * Features role-colored badges, status toggle, and refined styling.
 */

import { useState, useEffect, useCallback } from 'react';
import { X, UserCog, Loader2, ChevronDown, Shield, Check } from 'lucide-react';
import { useUpdateUser } from '../../hooks/useUsers';
import type { UserListItem, UserRole } from '@guild/shared';

interface UserFormProps {
  user: UserListItem;
  onClose: () => void;
}

// Role configuration with colors and descriptions
const roleConfig: Partial<Record<UserRole, { label: string; bg: string; text: string; dot: string; description: string }>> = {
  super_admin: {
    label: 'Super Admin',
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    dot: 'bg-amber-500',
    description: 'Cross-org camera management and all admin capabilities',
  },
  admin: {
    label: 'Admin',
    bg: 'bg-indigo-50',
    text: 'text-indigo-700',
    dot: 'bg-indigo-500',
    description: 'Full system access and user management',
  },
  manager: {
    label: 'Manager',
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    dot: 'bg-violet-500',
    description: 'Dashboard, assessments, and reports',
  },
  trainer: {
    label: 'Trainer',
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    dot: 'bg-blue-500',
    description: 'Training library and assignments',
  },
  operator: {
    label: 'Operator',
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    dot: 'bg-emerald-500',
    description: 'Training portal access only',
  },
};

const roleOptions: UserRole[] = ['admin', 'manager', 'trainer', 'operator'];

export function UserForm({ user, onClose }: UserFormProps) {
  const [fullName, setFullName] = useState(user.fullName);
  const [role, setRole] = useState<UserRole>(user.role as UserRole);
  const [isActive, setIsActive] = useState(user.isActive);
  const [error, setError] = useState<string | null>(null);
  const [isRoleDropdownOpen, setIsRoleDropdownOpen] = useState(false);

  const updateMutation = useUpdateUser();
  const isPending = updateMutation.isPending;

  // Reset form when user changes
  useEffect(() => {
    setFullName(user.fullName);
    setRole(user.role as UserRole);
    setIsActive(user.isActive);
    setError(null);
  }, [user]);

  // ESC to close
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !isPending) {
        onClose();
      }
    },
    [onClose, isPending]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setIsRoleDropdownOpen(false);
    if (isRoleDropdownOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isRoleDropdownOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!fullName.trim()) {
      setError('Name is required');
      return;
    }

    if (fullName.trim().length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: user.id,
        data: {
          fullName: fullName.trim(),
          role,
          isActive,
        },
      });
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const defaultRoleConfig = { label: 'Unknown', bg: 'bg-gray-100', text: 'text-gray-600', dot: 'bg-gray-400', description: 'Unknown role' };
  const currentRoleConfig = roleConfig[role] || defaultRoleConfig;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
        onClick={!isPending ? onClose : undefined}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50">
              <UserCog size={20} className="text-violet-600" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-gray-900">Edit User</h2>
              <p className="text-xs text-gray-500">Update user details and permissions</p>
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isPending}
            className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-5 flex items-center gap-2 rounded-xl bg-red-50 border border-red-100 px-4 py-3">
              <div className="h-2 w-2 rounded-full bg-red-500" />
              <p className="text-sm font-medium text-red-700">{error}</p>
            </div>
          )}

          <div className="space-y-5">
            {/* User Info Header */}
            <div className="flex items-center gap-3 pb-4 border-b border-gray-100">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-guild-green to-guild-green/80 text-white font-semibold">
                {user.fullName
                  .split(' ')
                  .map((n) => n[0])
                  .join('')
                  .toUpperCase()
                  .slice(0, 2)}
              </div>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {user.email}
                </p>
                <p className="text-xs text-gray-500">
                  Member since {new Date(user.createdAt).toLocaleDateString()}
                </p>
              </div>
            </div>

            {/* Full Name Field */}
            <div>
              <label
                htmlFor="fullName"
                className="block text-sm font-semibold text-gray-700 mb-2"
              >
                Full Name <span className="text-red-500">*</span>
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter full name"
                className="
                  w-full rounded-xl border border-gray-200 bg-white
                  px-4 py-3 text-sm
                  placeholder:text-gray-400
                  focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                  disabled:bg-gray-50 disabled:text-gray-500
                  transition-all duration-200
                "
                disabled={isPending}
                autoFocus
              />
            </div>

            {/* Role Field */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Role
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsRoleDropdownOpen(!isRoleDropdownOpen);
                  }}
                  disabled={isPending}
                  className="
                    w-full flex items-center justify-between
                    rounded-xl border border-gray-200 bg-white
                    px-4 py-3 text-sm text-left
                    focus:border-guild-green focus:outline-none focus:ring-2 focus:ring-guild-green/20
                    disabled:bg-gray-50 disabled:text-gray-500
                    transition-all duration-200
                  "
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`
                        inline-flex items-center gap-1.5 rounded-full px-2.5 py-1
                        text-xs font-semibold
                        ${currentRoleConfig.bg} ${currentRoleConfig.text}
                      `}
                    >
                      <span className={`h-1.5 w-1.5 rounded-full ${currentRoleConfig.dot}`} />
                      {currentRoleConfig.label}
                    </span>
                    <span className="text-gray-500 text-xs hidden sm:inline">
                      {currentRoleConfig.description}
                    </span>
                  </div>
                  <ChevronDown
                    size={16}
                    className={`text-gray-400 transition-transform ${isRoleDropdownOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {/* Dropdown */}
                {isRoleDropdownOpen && (
                  <div className="absolute z-10 mt-2 w-full rounded-xl border border-gray-100 bg-white shadow-xl overflow-hidden">
                    {roleOptions.map((roleOption) => {
                      const config = roleConfig[roleOption]!;
                      const isSelected = roleOption === role;
                      return (
                        <button
                          key={roleOption}
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setRole(roleOption);
                            setIsRoleDropdownOpen(false);
                          }}
                          className={`
                            w-full flex items-center justify-between px-4 py-3 text-left
                            hover:bg-gray-50 transition-colors
                            ${isSelected ? 'bg-gray-50' : ''}
                          `}
                        >
                          <div className="flex items-center gap-3">
                            <span
                              className={`
                                inline-flex items-center gap-1.5 rounded-full px-2.5 py-1
                                text-xs font-semibold
                                ${config.bg} ${config.text}
                              `}
                            >
                              <span className={`h-1.5 w-1.5 rounded-full ${config.dot}`} />
                              {config.label}
                            </span>
                            <span className="text-gray-500 text-xs">
                              {config.description}
                            </span>
                          </div>
                          {isSelected && (
                            <Check size={16} className="text-guild-green" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Status Toggle */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Account Status
              </label>
              <div className="flex items-center justify-between rounded-xl border border-gray-200 bg-white px-4 py-3">
                <div className="flex items-center gap-3">
                  <Shield
                    size={18}
                    className={isActive ? 'text-emerald-500' : 'text-gray-400'}
                  />
                  <div>
                    <p className="text-sm font-medium text-gray-900">
                      {isActive ? 'Active' : 'Inactive'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {isActive
                        ? 'User can sign in and access the platform'
                        : 'User is blocked from signing in'}
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsActive(!isActive)}
                  disabled={isPending}
                  className={`
                    relative inline-flex h-6 w-11 items-center rounded-full
                    transition-colors duration-200
                    focus:outline-none focus:ring-2 focus:ring-guild-green/20 focus:ring-offset-2
                    disabled:opacity-50 disabled:cursor-not-allowed
                    ${isActive ? 'bg-guild-green' : 'bg-gray-200'}
                  `}
                >
                  <span
                    className={`
                      inline-block h-4 w-4 transform rounded-full bg-white shadow-sm
                      transition-transform duration-200
                      ${isActive ? 'translate-x-6' : 'translate-x-1'}
                    `}
                  />
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="
                px-5 py-2.5 text-sm font-semibold text-gray-600
                rounded-xl hover:bg-gray-100
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors
              "
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isPending}
              className="
                inline-flex items-center gap-2 px-5 py-2.5
                text-sm font-semibold text-white bg-guild-green
                rounded-xl shadow-lg shadow-guild-green/20
                hover:bg-guild-green/95 hover:shadow-xl hover:shadow-guild-green/25
                disabled:opacity-50 disabled:cursor-not-allowed
                active:scale-[0.98]
                transition-all duration-200
              "
            >
              {isPending && <Loader2 size={16} className="animate-spin" />}
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
